import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Pricing.css';

/** GET /api/credits/pricing: every number on this page comes from the admin settings. */
interface Pricing {
  credits_per_package: number;
  package_price_usd: number;
  credits_per_analysis: number;
  price_per_credit_usd: number;
  price_per_analysis_usd: number;
  analyses_per_package: number;
  export_price_credits?: number;
  free_signup_credits?: number;
  purchases_enabled?: boolean;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const money = (v: number) => `$${v.toFixed(2)}`;

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState<Pricing | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/credits/pricing`)
      .then((r) => r.json())
      .then((result) => {
        if (result.status === 'success') setPricing(result.data);
        else setError(true);
      })
      .catch(() => setError(true));
  }, []);

  const handleGetStarted = () => navigate('/signup');

  if (!pricing && !error) {
    return (
      <div className="pricing-page">
        <div className="loading">
          <div className="spinner"></div>
          <span>Loading pricing information...</span>
        </div>
      </div>
    );
  }

  if (!pricing) {
    return (
      <div className="pricing-page">
        <div className="pricing-hero">
          <h1>Proven Pricing</h1>
          <p className="hero-description">
            Pricing could not be loaded right now. Please try again, or email{' '}
            <a href="mailto:info@use-proven.com">info@use-proven.com</a>.
          </p>
        </div>
      </div>
    );
  }

  const p = pricing;
  const exportCredits = p.export_price_credits ?? 0;
  const freeCredits = p.free_signup_credits ?? 0;

  return (
    <div className="pricing-page">
      <div className="pricing-hero">
        <h1>Proven Pricing</h1>
        <p className="hero-description">
          EUDR plot checks for rubber exporters. Pay only for what you use with prepaid credits.
        </p>
      </div>

      {/* Value Proposition */}
      <div className="value-section">
        <div className="value-cards">
          <div className="value-card">
            <div className="value-icon">■</div>
            <h3>Every result by plot code</h3>
            <p>Each finding is traced to the plot code in your own file, ready to pass on to your EU buyer.</p>
          </div>
          <div className="value-card">
            <div className="value-icon">🎯</div>
            <h3>Official reference data</h3>
            <p>Thai National Reserved Forests (RFD), Thai Protected Areas (MNRE) and the EU JRC forest map for 31 Dec 2020.</p>
          </div>
          <div className="value-card">
            <div className="value-icon">⚡</div>
            <h3>Fast results</h3>
            <p>Thousands of plots are checked in seconds; the PDF report with satellite maps follows in about a minute.</p>
          </div>
          <div className="value-card">
            <div className="value-icon">💰</div>
            <h3>No subscription</h3>
            <p>Pay per analysis with no setup fees or monthly charges.</p>
          </div>
        </div>
      </div>

      {/* Pricing Card */}
      <div className="pricing-section">
        <h2>Simple, Transparent Pricing</h2>
        <p className="pricing-subtitle">One credit package, used for analyses and exports</p>

        <div className="pricing-container">
          <div className="pricing-card featured">
            <div className="pricing-header">
              <h3>Credit Package</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">{p.package_price_usd.toFixed(p.package_price_usd % 1 ? 2 : 0)}</span>
                <span className="period">per package</span>
              </div>
            </div>

            <div className="pricing-features">
              <div className="feature"><span className="feature-icon">✓</span><span>{p.credits_per_package} credits</span></div>
              <div className="feature"><span className="feature-icon">✓</span><span>{p.analyses_per_package} complete EUDR analyses</span></div>
              <div className="feature"><span className="feature-icon">✓</span><span>{money(p.price_per_analysis_usd)} per analysis</span></div>
              <div className="feature"><span className="feature-icon">✓</span><span>PDF report with satellite maps</span></div>
              <div className="feature"><span className="feature-icon">✓</span><span>Interactive maps</span></div>
              <div className="feature"><span className="feature-icon">✓</span><span>Analysis history: reopen any earlier result</span></div>
              {exportCredits > 0 && (
                <div className="feature"><span className="feature-icon">✓</span><span>Buyer-format GeoJSON export: {exportCredits} credits per download</span></div>
              )}
              <div className="feature"><span className="feature-icon">✓</span><span>Credits never expire</span></div>
            </div>

            {freeCredits > 0 && (
              <div className="signup-info">
                <p className="signup-description">
                  New accounts get {freeCredits} free credits after verifying their email.
                </p>
              </div>
            )}

            <button onClick={handleGetStarted} className="btn primary cta-btn">
              Get Started Now
            </button>

            <p className="pricing-note">
              {p.purchases_enabled
                ? 'No setup fees • No monthly commitments • Credits added instantly'
                : 'To buy credits, email info@use-proven.com — we add them to your account.'}
            </p>
          </div>
        </div>
      </div>

      {/* Pricing Breakdown */}
      <div className="breakdown-section">
        <h2>What You Get</h2>
        <div className="breakdown-grid">
          <div className="breakdown-item">
            <div className="breakdown-number">{p.credits_per_package}</div>
            <div className="breakdown-label">Credits per Package</div>
            <div className="breakdown-description">Each credit is worth {money(p.price_per_credit_usd)}</div>
          </div>
          <div className="breakdown-item">
            <div className="breakdown-number">{p.credits_per_analysis}</div>
            <div className="breakdown-label">Credits per Analysis</div>
            <div className="breakdown-description">{money(p.price_per_analysis_usd)} per analysis, however many plots</div>
          </div>
          <div className="breakdown-item">
            <div className="breakdown-number">{p.analyses_per_package}</div>
            <div className="breakdown-label">Analyses per Package</div>
            <div className="breakdown-description">Run a new analysis whenever your plot list changes</div>
          </div>
          <div className="breakdown-item">
            <div className="breakdown-number">∞</div>
            <div className="breakdown-label">Credit Expiration</div>
            <div className="breakdown-description">Your credits never expire</div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>How does the credit system work?</h4>
            <p>
              A package of {p.credits_per_package} credits costs {money(p.package_price_usd)}. Each analysis uses{' '}
              {p.credits_per_analysis} credits{exportCredits > 0 ? ` and each JSON export download ${exportCredits}` : ''}.
              Credits never expire.
            </p>
          </div>
          <div className="faq-item">
            <h4>What does an analysis include?</h4>
            <p>
              Overlaps between your own plots, with Thai National Reserved Forests and Protected Areas, and with forest
              cover on 31 December 2020 (EU JRC map), plus a check of declared against mapped plot area. Results come as
              tables by plot code, interactive maps and a PDF report.
            </p>
          </div>
          <div className="faq-item">
            <h4>Do I need a subscription?</h4>
            <p>No. Buy credits when you need them and use them at your own pace.</p>
          </div>
          <div className="faq-item">
            <h4>What file formats are supported?</h4>
            <p>Excel (.xlsx), CSV and GeoJSON, with plot boundaries as polygons in WGS84 (WKT or GeoJSON). A template is provided on the Upload page.</p>
          </div>
          <div className="faq-item">
            <h4>How accurate is the analysis?</h4>
            <p>
              It uses official Thai government boundaries and the EU's 10 m forest map. All areas are measured in a metric
              projection for Thailand (UTM zone 47N). Results are only as accurate as your plot boundaries and the
              reference maps, so treat a flagged plot as something to check.
            </p>
          </div>
          <div className="faq-item">
            <h4>What if an analysis fails?</h4>
            <p>You are only charged when an analysis completes successfully, so a failed run costs nothing.</p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="cta-section">
        <h2>Ready to check your plots?</h2>
        <p>Upload your plot list and see every risk by plot code.</p>
        <div className="cta-buttons">
          <button onClick={handleGetStarted} className="btn primary large">
            Start Your Analysis
          </button>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
